import type {
  Account,
  AccountResponse,
  Cohort,
  Entry,
  Invite,
  Member,
  PatientSummary,
  Range,
} from '@fatigue/shared';
import { API_BASE_URL } from '../config';
import { idToken, renew, signOut, SignInError } from './session';

/**
 * The clinician API client.
 *
 * Every request carries the ID token — see `data/session.ts` for why that one
 * and not the access token — and `request` below owns the whole of the
 * expiry story, so no caller has to think about it.
 */
async function headers(): Promise<Record<string, string>> {
  return {
    'content-type': 'application/json',
    Authorization: `Bearer ${await idToken()}`,
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Prefers the API's own message over a status line.
 *
 * Every refusal in this system is deliberately worded to be shown to the
 * person who hit it — "That patient is not in your caseload", "Confirm your
 * own email address before inviting anyone" — and inventing a client-side
 * paraphrase would lose the one explanation that is actually accurate.
 */
async function fail(response: Response, path: string): Promise<never> {
  let message = `${path} failed (${response.status})`;
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string' && body.message !== '') {
      message = body.message;
    }
  } catch {
    // A non-JSON error body is not itself an error worth reporting over the
    // status we already have.
  }
  throw new ApiError(response.status, message);
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: await headers(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/**
 * A 401 is answered by renewing once and retrying, then by signing out.
 *
 * `idToken()` already refreshes ahead of expiry, so reaching here means
 * something else: a clock that moved, a token rejected for a reason the client
 * cannot see, or a refresh token that has itself run out. Exactly one retry —
 * a loop on a permanently invalid token would sit there re-asking forever
 * while the clinician watches a spinner.
 *
 * Note what is NOT retried: a 403. That is the tenancy answer — a patient
 * outside your account, or a cohort you are not assigned to — and it will say
 * the same thing however many times it is asked. Retrying it would turn a
 * clear refusal into a hang.
 */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await send(method, path, body);
  } catch (err) {
    // `idToken()` refuses when there is nothing left to refresh with.
    if (err instanceof SignInError) return giveUp();
    throw err;
  }

  if (response.status === 401) {
    try {
      await renew();
    } catch {
      return giveUp();
    }
    response = await send(method, path, body);
    if (response.status === 401) return giveUp();
  }

  if (!response.ok) return fail(response, path);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/**
 * Sign out, and never resolve.
 *
 * `signOut` reloads the page, so resolving or rejecting here would only give
 * a component the chance to render an error against a session that is already
 * gone. A promise that stays pending until the reload lands is the honest
 * shape of "this request has no answer any more".
 */
function giveUp(): Promise<never> {
  signOut();
  return new Promise<never>(() => {});
}

const getJson = <T,>(path: string): Promise<T> => request<T>('GET', path);

/* ------------------------------------------------------------------ caseload */

export async function listPatients(): Promise<readonly PatientSummary[]> {
  const body = await getJson<{ patients: readonly PatientSummary[] }>('/patients');
  return body.patients;
}

/**
 * Email a patient a link to choose a new password. The API sends it to the
 * address they sign in with; nothing here names one.
 */
export async function resetPatientAccess(patientId: string): Promise<void> {
  await request<unknown>('POST', `/patients/${encodeURIComponent(patientId)}/reset`);
}

/** Entries oldest first, which is what the chart wants. */
export async function listPatientEntries(
  patientId: string,
  range: Range,
): Promise<readonly Entry[]> {
  const query = rangeQuery(range);
  const body = await getJson<{ entries: readonly Entry[] }>(
    `/patients/${encodeURIComponent(patientId)}/entries${query}`,
  );
  return body.entries;
}

/**
 * "Whole course" sends no `from`, letting the server return everything it has
 * rather than the client guessing how long the course was.
 */
function rangeQuery(range: Range): string {
  if (range === 'course') return '';
  const from = new Date(Date.now() - range * 86_400_000).toISOString();
  return `?from=${encodeURIComponent(from)}`;
}

/* ------------------------------------------------------------------- cohorts */

/** The caller's own assignments. Empty for an owner who has not assigned themselves. */
export async function listMyCohorts(): Promise<readonly Cohort[]> {
  const body = await getJson<{ cohorts: readonly Cohort[] }>('/my/cohorts');
  return body.cohorts;
}

/** Every cohort in the account, with who runs it. Owner only. */
export async function listCohorts(): Promise<readonly Cohort[]> {
  const body = await getJson<{ cohorts: readonly Cohort[] }>('/cohorts');
  return body.cohorts;
}

export async function createCohort(name: string): Promise<Cohort> {
  const body = await request<{ cohort: Cohort }>('POST', '/cohorts', { name });
  return body.cohort;
}

export function assignClinician(cohortId: string, userId: string): Promise<void> {
  return request<void>(
    'PUT',
    `/cohorts/${encodeURIComponent(cohortId)}/clinicians/${encodeURIComponent(userId)}`,
  );
}

export function unassignClinician(cohortId: string, userId: string): Promise<void> {
  return request<void>(
    'DELETE',
    `/cohorts/${encodeURIComponent(cohortId)}/clinicians/${encodeURIComponent(userId)}`,
  );
}

/* ------------------------------------------------------------------- account */

export async function getAccount(): Promise<Account> {
  return (await getJson<AccountResponse>('/account')).account;
}

export async function setContactPhone(contactPhone: string): Promise<Account> {
  return (await request<AccountResponse>('PUT', '/account', { contactPhone })).account;
}

/* ------------------------------------------------------------------- members */

export async function listClinicians(): Promise<readonly Member[]> {
  const body = await getJson<{ members: readonly Member[] }>('/clinicians');
  return body.members;
}

export function suspendClinician(userId: string): Promise<{ member: Member }> {
  return request<{ member: Member }>('DELETE', `/clinicians/${encodeURIComponent(userId)}`);
}

/* --------------------------------------------------------------- invitations */

export async function listInvites(): Promise<readonly Invite[]> {
  const body = await getJson<{ invites: readonly Invite[] }>('/invites');
  return body.invites;
}

export async function inviteClinician(input: {
  email: string;
  displayName: string;
  cohortId?: string;
}): Promise<Invite> {
  const body = await request<{ invite: Invite }>('POST', '/clinicians', input);
  return body.invite;
}

export async function invitePatient(
  cohortId: string,
  input: { email: string; displayName: string; referenceCode: string },
): Promise<Invite> {
  const body = await request<{ invite: Invite }>(
    'POST',
    `/cohorts/${encodeURIComponent(cohortId)}/patients`,
    input,
  );
  return body.invite;
}
